import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'src/hooks/useEffectOnce';
import { OfflineDashboard } from 'src/views/OfflineDashboard';
import { getUser } from 'src/utils/Common';
import offlineAxios from 'src/utils/offlineAxios';

const OfflineDashboardContext = createContext(null);
const messageQueue = [];

function log(...args) {
    if (process.env.NODE_ENV === 'development') {
        console.log('%c[CLIENT]', 'color:#59cb92;', ...args);
    }
}

function getOfflineModeEnabledForUser(tenantId) {
    const enabledTenantIds = [46, 49];
    return enabledTenantIds.includes(tenantId);
}

export function OfflineDashboardProvider({ children }) {
    const [internetConnected, setInternetConnected] = useState(navigator.onLine)

    const [data, setData] = useState(null);
    const [dataLoading, setDataLoading] = useState();
    const [dataCaching, setDataCaching] = useState();

    const [submissions, setSubmissions] = useState();
    const [submissionsLoading, setSubmissionsLoading] = useState(false);
    const [submissionsCaching, setSubmissionsCaching] = useState(false);

    const [onlineRedirectBlocked, setOnlineRedirectBlocked] = useState(null);

    const worker = useRef(null);
    const user = JSON.parse(JSON.stringify(getUser()));
    const isOfflineModeEnabledForUser = getOfflineModeEnabledForUser(user?.TenantId);
    const postMessage = message => {
        if (worker.current) {
            worker.current.postMessage(message);
        } else {
            messageQueue.push(message);
        }
    };

    useEffect(() => {
        if (internetConnected === false && onlineRedirectBlocked === null) {
            setOnlineRedirectBlocked(false);
        }

        if (onlineRedirectBlocked) return;

        if (internetConnected === true && onlineRedirectBlocked === false) {
            window.location.href = '/';
        }
    }, [internetConnected, onlineRedirectBlocked]);

    const onReady = sw => {
        log('Service Worker ready');

        if (!navigator.serviceWorker.controller) {
            log('SW active but not controlling the page.');
            return;
        }

        worker.current = sw;
        while (messageQueue.length) {
            const message = messageQueue.shift();
            postMessage(message);
        }
    };

    const onMessage = event => {
        if (event.data.tag === 'INTERNET_STATUS') {
            setInternetConnected(event.data.online);
        }

        if (event.data.tag === 'GET_DATA_RES') {
            log('Data received');
            setData(event.data.data);
            setDataLoading(false);
        }

        if (event.data.tag === 'CACHE_DATA_RES') {
            log('Data cached successfully');
            setDataCaching(false);
            setData(prev => ({ ...prev, ...event.data.data }));
        }

        if (event.data.tag === 'GET_SUBMISSIONS_RES') {
            log('Submissions received');
            setSubmissions(prev => ({
                ...prev,
                ...event.data.submissions
            }));
            setSubmissionsLoading(false);
        }

        if (event.data.tag === 'CACHE_SUBMISSION_RES') {
            log('Submission cached successfully');
            setSubmissionsCaching(false);
            setSubmissions(prev => ({
                ...prev,
                [event.data.submissionKey]: event.data.submissionPayload
            }));
        }
    };

    useEffectOnce(async () => {
        if (!('serviceWorker' in navigator)) {
            log('Service Worker not supported.');
            return;
        }

        log('Service Worker supported. Registering...');

        navigator.serviceWorker.addEventListener('message', onMessage);

        navigator.serviceWorker.addEventListener('controllerchange', () => {
            log('Controller changed');
            if (navigator.serviceWorker.controller) {
                log('New controller');
                onReady(navigator.serviceWorker.controller);
            }
        });

        navigator.serviceWorker.ready.then(registration => {
            if (!navigator.serviceWorker.controller) {
                log('No SW controller');
                window.location.reload();
                return;
            }
            if (registration.active) onReady(registration.active);
        });
        return () => {
            navigator.serviceWorker.removeEventListener('message', onMessage);
        };
    });

    useEffectOnce(() => {
        const accessToken = localStorage.getItem('token');
        if (accessToken) {
            postMessage({ tag: 'SET_ACCESS_TOKEN', accessToken });
        }
        window.addEventListener('storage', event => {
            if (event.key === 'token') {
                postMessage({ tag: 'SET_ACCESS_TOKEN', accessToken: event.newValue });
            }
        });
    });

    function getData({ dataKeys } = {}) {
        log('Getting data');
        setDataLoading(true);
        postMessage({
            tag: 'GET_DATA',
            dataKeys
        });
    }

    function cacheData(dataPayload = {}) {
        log('Caching data');
        setDataCaching(true);
        postMessage({
            tag: 'CACHE_DATA',
            dataPayload
        });
    }

    function getSubmissions({ submissionKeys } = {}) {
        log('Getting submissions');
        setSubmissionsLoading(true);
        postMessage({
            tag: 'GET_SUBMISSIONS',
            submissionKeys
        });
    }

    function deleteSubmission(submissionKey) {
        log('Deleting submission');
        setSubmissionsCaching(true);
        postMessage({
            tag: 'DELETE_SUBMISSION',
            submissionKey
        });
    }

    function cacheSubmission(submissionKey, submissionPayload) {
        log('Caching submission');
        setSubmissionsCaching(true);
        postMessage({
            tag: 'CACHE_SUBMISSION',
            submissionKey,
            submissionPayload
        });
    }

    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isFileUploadPage = path.endsWith('/Fotograf') || path.endsWith('/Fotograf/');




    const renderChildren = isFileUploadPage ||
        ((internetConnected || internetConnected === null || !user) && onlineRedirectBlocked === null) ||
        !isOfflineModeEnabledForUser;


    return (
        <OfflineDashboardContext.Provider
            value={{
                internetConnected,
                user,

                data,
                dataLoading,
                dataCaching,
                getData,
                cacheData,

                submissions,
                submissionsLoading,
                submissionsCaching,
                getSubmissions,
                cacheSubmission,
                deleteSubmission,

                onlineRedirectBlocked,
                setOnlineRedirectBlocked
            }}
        >
            {renderChildren ? children : <OfflineDashboard />}
        </OfflineDashboardContext.Provider>
    );
}

export function useOfflineDashboard({ dataKeys, submissionKeys } = {}) {
    const context = useContext(OfflineDashboardContext);

    useEffect(() => {
        if (context.internetConnected !== false) return;
        context.getData({ dataKeys });
        context.getSubmissions({ submissionKeys });
    }, [context.internetConnected]);

    return context;
}

export function useOfflineDashboardDataCacher() {
    const { internetConnected, cacheData } = useContext(OfflineDashboardContext);

    useEffect(() => {
        if (!internetConnected) return;
        async function updateCache() {
            const workOrdersCacheData = await fetchWorkOrders();
            if (!Array.isArray(workOrdersCacheData)) return;
            log('Fetched work orders for cache');
            cacheData({ WorkOrders: workOrdersCacheData });
        }
        updateCache();
        const intervalId = setInterval(updateCache, 60 * 1000);
        return () => clearInterval(intervalId);
    }, [internetConnected]);
}

async function fetchWorkOrders() {
    let workOrdersData = [];

    try {
        const makeDefectLevels = (defects = []) => {
            const level1Map = new Map();
            const level2Map = new Map();

            return defects.reduce(
                (acc, defect) => {
                    const { CategoryLevel1Id, CategoryLevel1, CategoryLevel2Id, CategoryLevel2 } = defect;

                    if (!level1Map.has(CategoryLevel1Id)) {
                        level1Map.set(CategoryLevel1Id, true);
                        acc.level1.push({
                            level1Id: CategoryLevel1Id,
                            level1Desc: CategoryLevel1
                        });
                    }

                    if (!level2Map.has(CategoryLevel2Id)) {
                        level2Map.set(CategoryLevel2Id, true);
                        acc.level2.push({
                            level2Id: CategoryLevel2Id,
                            level2Desc: CategoryLevel2,
                            level1Id: CategoryLevel1Id,
                            level1Desc: CategoryLevel1
                        });
                    }

                    return acc;
                },
                { level1: [], level2: [] }
            );
        };

        const { data: workOrders = [] } = await offlineAxios.get('/Offline/GetOfflineWorkOrders');

        workOrdersData = await Promise.all(
            workOrders.map(async workOrder => {
                const consumerRes = await offlineAxios.get(`/Technician/GetConsumerWorkOrder/${workOrder.WorkOrderId}`);
                const consumer = consumerRes.data?.[0] || {};

                const consumerSecretNotesRes = await offlineAxios.get(
                    `/Technician/GetConsumerSecretNote/${consumer.ConsumerId}`
                );
                const consumerSecretNotes = consumerSecretNotesRes.data || [];

                const fileTypesRes = await offlineAxios.get(`/Technician/GetFileType`);
                const fileTypes = fileTypesRes.data || [];

                const filesRes = await offlineAxios.get(`/Technician/GetSiriusFileList/${consumer.ReceiptNo}`);
                const files = filesRes.data || [];

                return {
                    WorkOrderId: workOrder.WorkOrderId,
                    Defects: workOrder.Defects,
                    DefectLevels: makeDefectLevels(workOrder?.Defects),
                    Labours: workOrder.Labours,
                    Materials: workOrder.Materials,
                    brand: workOrder.Brand,
                    ModelCode: workOrder.ModelCode,
                    ProductId: workOrder.ProductId,
                    ProductMarketingCode: workOrder.ProductMarketingCode,
                    ProductionPeriod: workOrder.ProductionPeriod,
                    ProductionYear: workOrder.ProductionYear,
                    RepairCompletedDate: workOrder.RepairCompletedDate,
                    SerialNumber: workOrder.SerialNumber,
                    FileTypes: fileTypes,
                    Files: files,
                    CustomerFullName: consumer.CustomerFullName,
                    ReceiptNo: consumer.ReceiptNo,
                    IssueReason: consumer.IssueReason,
                    Status: consumer.Status,
                    ConsumerSecretNotes: consumerSecretNotes,
                    ConsumerTypeName: consumer.ConsumerTypeName,
                    Phones: consumer.Phones,
                    Addresses: consumer.Addresses,
                    EmployeeId: consumer.EmployeeId,
                    AppointmentDate: consumer.AppointmentDate,
                    ContactName: consumer.ContactName,
                    ContactPhone: consumer.ContactPhone,
                    WarrantyDate: consumer.WarrantyDate,
                    WarrantyTypeId: consumer.WarrantyTypeId,
                    LatestStatus: consumer.LatestStatus,
                    Notes: consumer.Notes,
                    IssueId: consumer.IssueId,
                    PaymentTypeId: consumer.PaymentTypeId,
                    ProjectCode: consumer.ProjectCode,
                    TaskTypeId: consumer.TaskTypeId,
                    WorkOrderComments: consumer.WorkOrderComments,
                    IssueReasonId: consumer.IssueReasonId,
                    StatusNote: consumer.StatusNote,
                    OpenDate: consumer.OpenDate,
                    Channel: consumer.Channel,
                    ConsumerConsultant: consumer.ConsumerConsultant,
                    WorkOrderDetails: consumer.WorkOrderDetails,
                    WorkOrderLabours: consumer.WorkOrderLabours,
                    WorkOrderMaterials: consumer.WorkOrderMaterials,
                    WorkOrderExtra: consumer.WorkOrderExtra,
                    WorkOrderTransport: consumer.WorkOrderTransport
                };
            })
        );
        return workOrdersData;
    } catch (err) {
        console.error('Error fetching offline work orders:', err);
    }
}
